import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("caos_access");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * The API wraps every JSON body in `{ success, status, message, data, timestamp }`.
 * Unwrapping here means call sites keep reading the payload directly — `data.id`
 * rather than `data.data.id` — so the envelope stays a transport concern instead
 * of leaking into ninety-odd components.
 *
 * Binary responses (PDFs, downloads, the import template) arrive as a Blob and
 * carry no envelope, so they are passed through untouched.
 */
const unwrap = (response) => {
  const body = response.data;
  const isEnvelope =
    body &&
    typeof body === "object" &&
    !(body instanceof Blob) &&
    !Array.isArray(body) &&
    "success" in body &&
    "timestamp" in body;
  if (!isEnvelope) return response;
  // `message` is kept alongside so a caller can surface the server's own wording.
  return { ...response, data: body.data, apiMessage: body.message };
};

/**
 * Pulls the human-readable reason out of a failed response.
 *
 * Errors carry `{ success: false, message, data? }`, where `data` holds per-field
 * validation messages when there are any. Those are joined so a form error reads
 * as a sentence rather than showing only the generic "Some fields need attention".
 */
export function errorMessage(error, fallback = "Something went wrong") {
  const body = error?.response?.data;
  if (!body) return error?.message || fallback;
  if (typeof body === "string") return body;
  const fields = body.data;
  if (fields && typeof fields === "object" && !Array.isArray(fields)) {
    const parts = Object.entries(fields).map(([field, msg]) => `${field} ${msg}`);
    if (parts.length) return parts.join(", ");
  }
  return body.message || fallback;
}

let refreshing = null;
api.interceptors.response.use(
  unwrap,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && localStorage.getItem("caos_refresh")) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = axios.post(`${API}/auth/refresh`, {
            refresh_token: localStorage.getItem("caos_refresh"),
          });
        }
        const { data } = await refreshing;
        // Raw axios, not `api`, so the response interceptor never ran and the
        // envelope is still wrapped around the tokens.
        const tokens = data?.data ?? data;
        refreshing = null;
        localStorage.setItem("caos_access", tokens.access_token);
        localStorage.setItem("caos_refresh", tokens.refresh_token);
        original.headers.Authorization = `Bearer ${tokens.access_token}`;
        return api(original);
      } catch (e) {
        refreshing = null;
        localStorage.removeItem("caos_access");
        localStorage.removeItem("caos_refresh");
        localStorage.removeItem("caos_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;
